import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import "./FrameOrders.css";

const STATUS_LABELS = {
  awaiting_payment: "Awaiting Payment",
  paid: "Paid",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const PAYOUT_LABELS = {
  not_started: "Not Started",
  in_progress: "In Progress",
  paid: "Paid",
};

function FrameOrders() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [shipModal, setShipModal] = useState(null);
  const [shipForm, setShipForm] = useState({ carrierName: "", trackingNumber: "" });
  const [shipping, setShipping] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchOrders = useCallback(async (p, status) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: p, limit: 20 });
      if (status) qs.set("status", status);
      const res = await fetch(`${API_BASE_URL}/api/photo-frame/orders?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setItems(data.items || []);
      setTotalPages(data.totalPages || 1);
    } catch {
      setMessage({ text: "Failed to load orders", type: "error" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchOrders(page, statusFilter);
  }, [page, statusFilter, fetchOrders]);

  useEffect(() => {
    if (message.text) {
      const t = setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const handleDownload = async (order) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/photo-frame/orders/${order.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = order.original_filename || `${order.order_number}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setMessage({ text: "Failed to download photo", type: "error" });
    }
  };

  const openShipModal = (order) => {
    setShipModal(order);
    setShipForm({ carrierName: "", trackingNumber: "" });
  };

  const handleShip = async () => {
    if (!shipForm.carrierName.trim() || !shipForm.trackingNumber.trim()) return;
    setShipping(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/photo-frame/orders/${shipModal.id}/ship`, {
        method: "PATCH", headers, body: JSON.stringify(shipForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to mark as shipped");
      setMessage({ text: "Order marked as shipped", type: "success" });
      setShipModal(null);
      fetchOrders(page, statusFilter);
    } catch (err) {
      setMessage({ text: err.message || "Failed to mark as shipped", type: "error" });
    } finally {
      setShipping(false);
    }
  };

  const handleDeliver = async (order) => {
    if (!window.confirm(`Mark #${order.order_number} as delivered?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/photo-frame/orders/${order.id}/deliver`, { method: "PATCH", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to mark as delivered");
      setMessage({ text: "Order marked as delivered", type: "success" });
      fetchOrders(page, statusFilter);
    } catch (err) {
      setMessage({ text: err.message || "Failed to mark as delivered", type: "error" });
    }
  };

  return (
    <div className="fo-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="fo-main">
        <header className="fo-header">
          <div className="fo-header-left">
            <button className="fo-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="fo-page-title">📦 Photo Frame Orders</h1>
          </div>
        </header>

        <main className="fo-content">
          {message.text && <div className={`fo-message fo-message--${message.type}`}>{message.text}</div>}

          <div className="fo-info-banner">
            <span className="fo-info-banner-icon">💰</span>
            <div>
              <strong>How payouts work:</strong> once the courier delivers a frame, mark that order <strong>Delivered</strong> below.
              We cross-check each delivery, then settle payouts on a weekly/monthly cycle — track where each order stands in the
              <strong> Payout Status</strong> column.
            </div>
          </div>

          <div className="fo-toolbar">
            <select className="fo-filter" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              <option value="paid">Paid</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>

          {loading ? (
            <p style={{ color: "#666" }}>Loading...</p>
          ) : items.length === 0 ? (
            <p style={{ color: "#666" }}>No orders yet.</p>
          ) : (
            <div className="fo-table-wrap">
              <table className="fo-table">
                <thead>
                  <tr>
                    <th>Photo</th>
                    <th>Booking ID</th>
                    <th>File Name</th>
                    <th>Event</th>
                    <th>Guest Name</th>
                    <th>Contact</th>
                    <th>Address</th>
                    <th>Frame</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Payout Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((o) => (
                    <tr key={o.id}>
                      <td>{o.thumbnail_url && <img src={o.thumbnail_url} alt="" className="fo-thumb" />}</td>
                      <td className="fo-mono">{o.order_number}</td>
                      <td>{o.original_filename || "—"}</td>
                      <td><Link to={`/qr-sharing/${o.qr_gallery_id}`} className="fo-link">{o.gallery_name}</Link></td>
                      <td>{o.guest_name}</td>
                      <td>{o.guest_phone}{o.guest_email ? <><br /><span className="fo-sub">{o.guest_email}</span></> : null}</td>
                      <td className="fo-address-cell">
                        {o.shipping_address_line1}{o.shipping_address_line2 ? `, ${o.shipping_address_line2}` : ""}, {o.shipping_city}, {o.shipping_state} {o.shipping_postal_code}, {o.shipping_country}
                      </td>
                      <td>{o.frame_label}<br /><span className="fo-sub">{o.frame_width_inches}" × {o.frame_height_inches}"</span></td>
                      <td>
                        ₹{parseFloat(o.total_amount).toFixed(0)}<br />
                        <span className="fo-sub">payout ₹{parseFloat(o.studio_price).toFixed(0)}{parseFloat(o.shipping_cost) > 0 ? ` + ₹${parseFloat(o.shipping_cost).toFixed(0)} shipping` : ""}</span>
                      </td>
                      <td>
                        <span className={`fo-status fo-status--${o.status}`}>{STATUS_LABELS[o.status] || o.status}</span>
                        {(o.status === "shipped" || o.status === "delivered") && o.carrier_name && (
                          <div className="fo-sub" style={{ marginTop: 4 }}>{o.carrier_name} · AWB {o.tracking_number}</div>
                        )}
                      </td>
                      <td><span className={`fo-payout fo-payout--${o.payout_status}`}>{PAYOUT_LABELS[o.payout_status] || o.payout_status}</span></td>
                      <td className="fo-actions-cell">
                        <button className="fo-btn" onClick={() => handleDownload(o)}>Download</button>
                        {o.status === "paid" && (
                          <button className="fo-btn fo-btn--primary" onClick={() => openShipModal(o)}>Mark Shipped</button>
                        )}
                        {o.status === "shipped" && (
                          <button className="fo-btn fo-btn--primary" onClick={() => handleDeliver(o)}>Mark Delivered</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="fo-pagination">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
            </div>
          )}
        </main>
      </div>

      {shipModal && (
        <div className="fo-modal-backdrop" onClick={() => setShipModal(null)}>
          <div className="fo-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mark #{shipModal.order_number} as Shipped</h3>
            <label className="fo-modal-label">Courier Name</label>
            <input
              className="fo-input"
              placeholder="e.g. Delhivery, India Post, BlueDart"
              value={shipForm.carrierName}
              onChange={(e) => setShipForm((p) => ({ ...p, carrierName: e.target.value }))}
            />
            <label className="fo-modal-label">AWB Number</label>
            <input
              className="fo-input"
              placeholder="Airway bill / tracking number"
              value={shipForm.trackingNumber}
              onChange={(e) => setShipForm((p) => ({ ...p, trackingNumber: e.target.value }))}
            />
            <div className="fo-modal-actions">
              <button className="fo-btn" onClick={() => setShipModal(null)}>Cancel</button>
              <button className="fo-btn fo-btn--primary" onClick={handleShip} disabled={shipping}>
                {shipping ? "Saving..." : "Confirm Shipped"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FrameOrders;
